export type CoTestingLocationDocument = {
  id: string;
  title: string;
  filePath: string;
  notes: string[];
};

export type CoTestingLocationListing = {
  id: string;
  documentId: string;
  providerName: string;
  addressLine1: string;
  city: string;
  state: "CO";
  postalCode: string;
  phoneNumber: string;
  rawPhoneNumber: string;
  hours: string | null;
  price: string | null;
  notes: string[];
};

function normalizePhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "");
  const nationalDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (nationalDigits.length !== 10) {
    throw new Error(`Expected a 10-digit US phone number, received "${value}".`);
  }
  return `+1${nationalDigits}`;
}

function normalizePostalCode(value: string): string {
  const digits = value.replace(/\D/g, "");
  return digits.slice(0, 5);
}

function titleCase(value: string): string {
  return value.toLowerCase().replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function createListing(input: {
  documentId: string;
  providerName: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  rawPhoneNumber: string;
  hours?: string | null;
  price?: string | null;
  notes?: readonly string[];
}): CoTestingLocationListing {
  const phoneNumber = normalizePhoneNumber(input.rawPhoneNumber);
  const city = titleCase(input.city);
  return {
    id: `${input.documentId}-${slugify(input.providerName)}-${slugify(city)}-${phoneNumber.slice(-4)}`,
    documentId: input.documentId,
    providerName: input.providerName,
    addressLine1: input.addressLine1,
    city,
    state: "CO",
    postalCode: normalizePostalCode(input.postalCode),
    phoneNumber,
    rawPhoneNumber: input.rawPhoneNumber,
    hours: input.hours ?? null,
    price: input.price ?? null,
    notes: input.notes ? [...input.notes] : [],
  };
}

export const coTestingLocationDocuments: CoTestingLocationDocument[] = [
  {
    id: "jefferson-county-ua-testing-sites",
    title: "Jefferson County Pretrial Services UA testing locations",
    filePath: "docs/UA-testing/CO/Jefferson County/testing_locations.pdf",
    notes: ["The source warns that prices and hours are subject to change."],
  },
  {
    id: "weld-county-approved-sites",
    title: "Weld County approved Sentry drug testing collection sites",
    filePath: "docs/UA-testing/CO/Weld County/testing_locations.pdf",
    notes: ["This document lists all approved collection sites for Weld County Pretrial Services."],
  },
  {
    id: "intervention-drug-testing-hours",
    title: "Intervention drug testing locations and hours",
    filePath: "docs/UA-testing/CO/Weld County/testing_locations.2.pdf",
    notes: ["This document adds location-specific hours for Intervention and ICCS sites."],
  },
];

const weldApprovedSiteRows = [
  ["First Step Recovery", "595 14th Street", "Burlington", "80807", "719.346.6060"],
  ["Adult Youth Counseling Services", "1012 Main Street", "Canon City", "81212", "719.276.1311"],
  ["Canon City Counseling", "224 N. Cottonwood", "Canon City", "81212", "719.276.0292"],
  ["Rocky Mtn. Behavioral Health", "3239 Independence Road", "Canon City", "81212", "719.275.7650"],
  ["RMOMS Castle Rock", "619 Wilcox Street", "Castle Rock", "80104", "303.325.2780"],
  ["A Turning Point", "5160 N Union Blvd", "Colorado Springs", "80918", "719.550.1011"],
  ["Centennial Mental Health Center", "606 Main Street", "Limon", "80828", "719.775.2313"],
  ["Intervention Pueblo", "801 West 4th Street, Ste 101", "Pueblo", "81003", "719.543.1000"],
  [
    "KCG Drug Alcohol Solutions",
    "28577 Buffalo Park Rd STE 100",
    "Evergreen",
    "80439",
    "303-670-2269",
  ],
  ["Day Reporting Center", "699 Summit, Unit K", "Frisco", "80443", "970-668-9900"],
  ["3 Peak Testing", "7405 West Hwy 50, Ste 122", "Salida", "I 81201", "I 719.207.4111"],
  ["Compliance Drug Testing", "641 E. Main Street", "Cortez", "81321", "970.565.9515"],
  ["Cortez Addiction Recovery Service", "35 North Ash Street", "Cortez", "81321", "970.565.4109"],
  ["Delta Drug Testing", "239 Palmer Street", "Delta", "81416", "970.874.9731"],
  ["The Place I Go", "228 Meeker Street", "Delta", "81416", "970.216.1983"],
  ["Detox Center", "1125 Three Springs Blvd", "Durango", "81301", "970.259.8732"],
  ["Hilltop House", "1050 Avenida Del Sol", "Durango", "81301", "970.247.1342"],
  ["Peaceful Spirit", "296 Mouache Circle", "Ignacio", "81137", "970.563.4555"],
  ["Drug Testing Inc.", "26 South Stough", "Montrose", "81401", "970.249.1113"],
  ["KCG Drug Alcohol Solutions", "8120 Sheridan Blvd STE B107", "Arvada", "80003", "303-261-8190"],
  ["Mile High Drug Test", "11059 E Bethany Drive, Suite 210", "Aurora", "80014", "720.491.1424"],
  ["RMOMS Aurora", "14707 E 2nd Ave #100", "Aurora", "80011", "303.367.1678"],
  ["Wiz-Quiz - Aurora", "2101 S. Blackhawk St., Suite 160", "Aurora", "80014", "303.873.7155"],
  ["Boulder Alcohol Ed Center", "1525 Spruce St, Suite 100", "Boulder", "80302", "303.444.6143"],
  ["Intervention Boulder", "5600 Arapahoe Avenue, Ste 100", "Boulder", "80303", "303.544.1840"],
  ["Wiz Quiz Boulder", "737 29th St.", "Boulder", "80303", "720.975.4114"],
  ["IDEA Brighton", "1295 E Bridge St", "Brighton", "80601", "303.659.9440"],
  ["Intervention Brighton", "647 E. Bridge Street", "Brighton", "80601", "303.654.0555"],
  ["RMOMS Brighton", "191 S. Telluride #6", "Brighton", "80601", "303.659.8662"],
  [
    "Metro Testing & Evaluation",
    "7000 W 117th Avenue, Unit B",
    "Broomfield",
    "80020",
    "303.465.2735",
  ],
  ["Wiz- Quiz - Centennial", "2305 E Arapahoe Rd Ste 147", "Centennial", "80122", "303-738-1140"],
  ["Acacia Counseling", "190 E 9th Ave #150", "Denver", "80203", "303-861-9378"],
  ["IDEA Denver", "2370 W Alameda Ave STE 8", "Denver", "80223", "303.477.8280"],
  ["Intervention Golden", "17720 S. Golden Road", "Golden", "80401", "303.278.9600"],
  [
    "Men & Women Seeking Empower",
    "1300 Plaza Ct. North, Unit 102",
    "Lafayette",
    "80026",
    "303.665.7037",
  ],
  ["ICCS Kendall", "1651 Kendall Street", "Lakewood", "80214", "303.232.4002"],
  ["Wiz Quiz", "7585 W. Arkansas #B", "Lakewood", "80232", "303.914.1045"],
  [
    "You're in Luck Drug & Alcohol",
    "1864 S Wadsworth Blvd #3",
    "Lakewood",
    "80232",
    "720.746.7119",
  ],
  ["RMS Northglenn", "2100 E 112th Ave", "Northglenn", "80233", "303.657.1402"],
  ["HEART Counseling", "10327 Washington St.", "Thornton", "80229", "720.379.6995"],
  [
    "Intervention Westminster",
    "12130 Pennsylvania St Ste 102",
    "Thornton",
    "80241",
    "303.450.6000",
  ],
  ["Precise Monitoring", "10370 Colorado Blvd", "Thornton", "80229", "303.920.5959"],
  ["HUBS", "8120 Sheridan Blvd", "Westminster", "80003", "303.487.6848"],
  ["RMS Westminster", "12201 N. Pecos Suite #400", "Westminster", "80031", "303.657.1405"],
  ["Centennial Mental Health Akron", "871 E. 1st Street", "Akron", "80720", "970.345.2254"],
  ["LCCCD Day Reporting", "2255 Midpoint Drive", "Fort Collins", "80524", "970.498.7510"],
  ["RMOMS Fort Collins #1", "3000 South College, #104", "Fort Collins", "80525", "970.206.9342"],
  ["UA/BA", "116 W Harvard Street Ste. 3", "Fort Collins", "80521", "970.292.8882"],
  ["Advatage Treatment Center (ATC)", "219 E Railroad Ave", "Fort Morgan", "80701", "970.867.2086"],
  [
    "Centennial Mental Health Ft Morgan",
    "821 E Railroad Ave",
    "Fort Morgan",
    "80701",
    "970.867.4924",
  ],
  ["GEO Reentry Services", "810 9th Street, Suite 100", "Greeley", "80631", "970.395.0333"],
  ["ICCS Weld", "1101 H Street", "Greeley", "80631", "970.584.2520"],
  ["Intervention Greeley", "920 11th Avenue", "Greeley", "80631", "970.584.2500"],
  ["UA/BA", "3400 W 16th St, Bldg 1, Ste 1-D", "Greeley", "80634", "970-292-8882"],
  [
    "Centennial Mental Health Julesburg",
    "118 West Third Street",
    "Julesburg",
    "80737",
    "970.474.3769",
  ],
  ["Intervention Longmont", "310 Lashley St, Suite 108", "Longmont", "80501", "303.772.4494"],
  ["RMOMS Longmont", "1630 N Main Street", "Longmont", "80501", "303.485.6896"],
  ["UA/BA", "700 Ken Pratt Suite 121", "Longmont", "80501", "970.292.8882"],
  ["A New Perspective Loveland", "2017 W. Eisenhower Blvd.", "Loveland", "80537", "970.461.0978"],
  ["Day Reporting Center", "810 E. 10th Street STE 140", "Loveland", "80537", "970.619.4372"],
  ["UA/BA", "1907 Boise Ave #2", "Loveland", "80537", "970.292.8882"],
  ["Advantage Treatment Center", "12220 Highway 61", "Sterling", "80751", "970.522.7383"],
  ["Centennial Mental Health Sterling", "211 West Main", "Sterling", "80751", "970.522.4392"],
  ["Centennial Mental Health Wray", "365 W 2nd Street", "Wray", "80758", "970.332.3133"],
  ["Centennial Mental Health (Females)", "215 South Ash", "Yuma", "80759", "970.848.5412"],
] as const;

const interventionHoursRows = [
  {
    providerName: "Intervention Thornton",
    addressLine1: "12130 Pennsylvania St, Ste 102",
    city: "Thornton",
    postalCode: "80241",
    rawPhoneNumber: "(303) 450-6000",
    hours: "Mon - Fri 7:30am - 5pm; Closed for lunch 12 - 12:30",
    notes: ["Please enter through side door."],
  },
  {
    providerName: "Intervention Boulder",
    addressLine1: "2639 Spruce St",
    city: "Boulder",
    postalCode: "80302",
    rawPhoneNumber: "(303) 544-1840",
    hours: "Monday - Friday 7am - 4pm; Closed daily from 12pm - 1pm for lunch",
    notes: [],
  },
  {
    providerName: "Intervention Brighton",
    addressLine1: "647 E. Bridge Street",
    city: "Brighton",
    postalCode: "80601",
    rawPhoneNumber: "(303) 654-0555",
    hours: "Monday - Friday 8am - 5pm; Closed daily from 12pm - 1pm for lunch",
    notes: [],
  },
  {
    providerName: "Intervention Golden",
    addressLine1: "17301 W. Colfax Ave, Suite 265",
    city: "Golden",
    postalCode: "80401",
    rawPhoneNumber: "(303) 278-9600",
    hours: "Monday - Friday 8:00am - 5pm; Closed 12 - 1 for lunch",
    notes: [],
  },
  {
    providerName: "Intervention Greeley",
    addressLine1: "920 11th Avenue",
    city: "Greeley",
    postalCode: "80631",
    rawPhoneNumber: "(970) 584-2500",
    hours: "Monday - Friday 7am - 6:00pm",
    notes: [],
  },
  {
    providerName: "Intervention Longmont",
    addressLine1: "624 S Sunset St",
    city: "Longmont",
    postalCode: "80501",
    rawPhoneNumber: "(303) 772-4494",
    hours:
      "Monday - Friday 7am - 5pm; Monitoring Hours: 7am - 4pm; Closed daily from 12 - 1 for lunch",
    notes: [],
  },
  {
    providerName: "Intervention Pueblo",
    addressLine1: "801 West 4th Street, Suite 101",
    city: "Pueblo",
    postalCode: "81003",
    rawPhoneNumber: "(719) 543-1000",
    hours: "Monday - Friday 7:30am - 5:00 pm",
    notes: ["Clients must be signed in by 4:45."],
  },
  {
    providerName: "ICCS - Kendall",
    addressLine1: "1651 Kendall Street",
    city: "Lakewood",
    postalCode: "80214",
    rawPhoneNumber: "(720) 544-5547",
    hours: "Out Client Services Monday-Friday; Monitoring Hours: 3:30pm - 12am",
    notes: ["Clients must be signed in by 11pm.", "Adult monitoring only."],
  },
  {
    providerName: "ICCS - Pueblo",
    addressLine1: "1901 North Hudson",
    city: "Pueblo",
    postalCode: "81001",
    rawPhoneNumber: "(719) 569-3020",
    hours: "Monday - Friday 6pm - 11:30pm; Saturday & Sunday 8am - 11:30pm",
    notes: ["Adult monitoring only."],
  },
  {
    providerName: "ICCS - Weld",
    addressLine1: "1101 H Street",
    city: "Greeley",
    postalCode: "80631",
    rawPhoneNumber: "(970) 584-2520",
    hours: "Open 6pm - 9pm for UAs",
    notes: ["Must be signed in by 8:30 pm.", "Adult monitoring only."],
  },
] as const;

const jeffersonSiteRows = [
  [
    "KCG Drug Alcohol Solution",
    "8120 Sheridan Blvd B-107",
    "ARVADA",
    "303-261-8190",
    "M-F 8:30-4:30pm",
    "$28",
  ],
  ["RMS ALAMOSA", "626 4TH ST", "ALAMOSA", "719-589-3243", "M-F 8-5, CLOSED 12 - 1", "Varies"],
  [
    "RMS AURORA",
    "14707 E. 2nd Ave 100 #340",
    "AURORA",
    "303-367-1678",
    "M-F 7-7 / Sat 8-12",
    "18.50-",
  ],
  [
    "RMS BRIGHTON",
    "191 Telluride St #6",
    "BRIGHTON",
    "303-659-8662",
    "M-F: 8:30am-5:30pm, CLOSED 12-1",
    "$15/Oral swab $20",
  ],
  [
    "INTERVENTION",
    "5600 Arapahoe Ave # 100",
    "BOULDER",
    "303- 544-1840",
    "M-F 7-4, CLOSED 12-1",
    "$15-22/OralSwabs$40",
  ],
  [
    "UABACO BOULDER",
    "2850 Iris Ave Suite N",
    "BOULDER",
    "970-292-8882",
    "M-S 11-9; Sun 11-6",
    "$18 exact cash only",
  ],
  [
    "INTERVENTION",
    "647 E Bridge Street",
    "BRIGHTON",
    "303-654-0555",
    "M-F 8-4:45, CLOSED 11:45 - 1",
    "$23",
  ],
  [
    "RMS CASTLE ROCK",
    "3750 Dacoro Ln., Building 1, Suite 140",
    "CASTLE ROCK",
    "303-325-2780",
    "M-F: 8am-5pm, CLOSED 12-1",
    "$18.50-",
  ],
  [
    "A Turning Point of Colorado Springs",
    "5160 N. Union Blvd",
    "Colorado Springs",
    "719-550-1011",
    "M-Thur 9am-8pm; Fri 10a,-6pm; Sat 8am- 2pm, SUNDAYS CLOSED",
    "$20/ Oral swabs $30",
  ],
  [
    "Heritage Outpatient Treatment",
    "1602 S. Parker Rd Ste 214",
    "Denver",
    "720-748-0523",
    "Mon, Tues, Thurs 4-5:30pm, 7:30-8pm; Wed, Fri 4pm-8pm; Sat: 1pm-5pm; Closed Sun",
    "$20/Oral Swabs $15/ Fentanyl $25",
  ],
  [
    "RMS DENVER",
    "930 W 7th Ave Suite A",
    "Denver",
    "303-825-3061",
    "M-F 10AM - 7PM; Closed Sun",
    "$18.50-",
  ],
  [
    "SPANISH CLINIC (Spanish Speaking)",
    "4200 Morrison Rd Unit 8",
    "Denver",
    "303-934-3040",
    "M-TH 5-8/ F 2-6 (Closed Daily 1-2)",
    "$18-26",
  ],
  [
    "RMS CENTENNIAL",
    "14485 E Fremont Ave",
    "ENGLEWOOD",
    "303-858-8550",
    "M-F 8-5, CLOSED 1-2",
    "$18.50-",
  ],
  [
    "KCG Drug Alcohol Solution",
    "28577 Buffalo Park Rd # 110",
    "EVERGREEN",
    "303-670-2269",
    "M-F 8:30-3",
    "$28",
  ],
  [
    "LCCCD DAY REPORTING CENTER",
    "2255 Midpoint Dr",
    "FORT COLLINS",
    "970-498-7510",
    "M-F 10-6:45, Sat/Sun: 8-3:45",
    "$15/ Oral Swabs $25",
  ],
  [
    "RMS FT. COLLINS",
    "3000 S. College #104",
    "FT. COLLINS",
    "970-206-9342",
    "M-F 9-6, CLOSED 1-2",
    "$18.50-",
  ],
  [
    "UABACO FT. COLLINS",
    "116 W Harvard St #3",
    "FT. COLLINS",
    "970-292-8882",
    "M-S 11-8:50; Sun 11-5:50",
    "$15-20 exact cash",
  ],
  [
    "Alpine Springs Counseling & Testing Center",
    "319 14th St",
    "GLENWOOD SPRINGS",
    "970-945-7858",
    "Open M-F 4:00-7:00 PM; call ahead: 970-945-8323",
    "$25",
  ],
  [
    "MCC Drug & Alcohol Drug & Alcohol Screening",
    "2001 Blake Ave 2D",
    "GLENWOOD SPRINGS",
    "970-945-7772",
    "M-Thur 8am-5pm, Fri 8am-4pm",
    "$55",
  ],
  [
    "INTERVENTION",
    "17301 W. Colfax Ave Ste 265",
    "GOLDEN",
    "303-278-9600",
    "M-F 8-4:30, CLOSED 12 - 1",
    "$21",
  ],
  [
    "ICCS WELD COUNTY",
    "1101 H Street",
    "GREELEY",
    "970-584-2520",
    "M-F 6PM-8:30PM; Weekends 8:30AM-8:30PM; No male staff until afternoon on weekends",
    "$21",
  ],
  ["INTERVENTION", "920 11th Ave", "GREELEY", "970-584-2500", "M-F 7-6, CLOSED 12 - 1", "$21"],
  [
    "UABACO Greeley",
    "3400 W 16th St Bld 1 #1-D",
    "GREELEY",
    "970-292-8882",
    "M-F 11-8:50",
    "$15-20 exact cash",
  ],
  [
    "MCC Drug & Alcohol Drug & Alcohol Screening",
    "1330 N. 12th St",
    "Grand Junction",
    "970-256-7772",
    "M-Thur 8am-5pm, Fri 8am-4pm",
    "$55",
  ],
  [
    "RMS - Grand Junction",
    "2531 W Pinyon Ave",
    "Grand Junction",
    "970-245-5119",
    "M, W, F - 7 - 4; T/Th - 8:30 - 5:30 (closed 1-2)",
    "$22.50; $27.45 oral swabs (cc or exact change)",
  ],
  [
    "5280 Drug Testing",
    "6053 S Quebec St #101",
    "GREENWOOD VILLAGE",
    "720-476-4970",
    "M-F 9-7:30",
    "$20",
  ],
  [
    "Men & Women Seeking Empowerment",
    "1300 Plaza Ct N #102",
    "Lafayette",
    "303-665-7037",
    "Males - M-F 1pm-7:00 Females - M-F 8AM-2PM",
    "$16",
  ],
  [
    "ICCS",
    "1651 KENDALL ST",
    "LAKEWOOD",
    "720-544-5557",
    "M-F 3:30PM - 11PM; Open on holidays",
    "$21 cash only",
  ],
  [
    "KCG Drug Alcohol Solution",
    "1864 s Wadsworth Blvd Unit 3",
    "LAKEWOOD",
    "303-670-2269",
    "M-F 9-am-4:30pm",
    "$28",
  ],
  [
    "GENESIS COUNSELING",
    "8 W Dry Creek Circle Ste 155",
    "LITTLETON",
    "720-309-1588",
    "FEMALES ONLY M 12P-4:30P; T-Thurs 9A-4:30P; Fri 9A-1P",
    "$22 cash, $30 credit",
  ],
  [
    "RMS LITTLETON",
    "191 E. Orchard Rd #205",
    "LITTLETON",
    "303-789-9800",
    "M-F 8-5, CLOSED 12 - 1",
    "$18.50.",
  ],
  [
    "INTERVENTION",
    "390 Lashley Street #100",
    "LONGMONT",
    "303-772-4494",
    "M-F 7-4:30 CLOSED 12 - 1",
    "$25",
  ],
  [
    "RMS LONGMONT",
    "1630 N Main St. Suite 103",
    "LONGMONT",
    "303-485-6896",
    "M-F 9-6, CLOSED 1-2",
    "$18.50-",
  ],
  [
    "UABACO LONGMONT",
    "700 Ken Pratt Blvd #121",
    "LONGMONT",
    "970-292-8882",
    "M-S 11-8:50; Sun 11-5:50",
    "$15-20 exact cash",
  ],
  [
    "UABACO LOVELAND",
    "1907 Boise Ave #2",
    "LOVELAND",
    "970-292-8882",
    "M-S 11-8:50; Sun 11-5:50",
    "$15-20 exact cash",
  ],
  [
    "ACACIA COUNSELING",
    "11150 Huron Street #207",
    "NORTHGLENN",
    "303-438-9730",
    "Hours vary, call ahead for UA test; CLOSED Fri and Sun",
    "$15",
  ],
  [
    "RMS NORTHGLENN",
    "2100 E. 112th Avenue Unit 1",
    "NORTHGLENN",
    "303-657-1405",
    "M-F 7-7, SAT 8am-12pm",
    "18.50.",
  ],
  [
    "ICCS PUEBLO",
    "1901 N. Hudson Ave",
    "PUEBLO",
    "719-569-3020",
    "M-F FROM 6:00 PM-11:30 PM, Sat & Sun 8 AM-11:30 PM",
    "$21+",
  ],
  [
    "INTERVENTION",
    "801 W 4th St Ste 101",
    "PUEBLO",
    "719-543-1000",
    "MON-FRI 7:30-5:30 PM",
    "$15-25",
  ],
  [
    "RMS PUEBLO",
    "807 W 4th St",
    "PUEBLO",
    "719-543-7835",
    "M-F 8-6/ Sat 9:30-6, CLOSED 1:30-2",
    "$18.50-",
  ],
  [
    "Alpine Springs Counseling & Testing Center",
    "1530 Railroad Ave Suite A",
    "RIFLE",
    "720-735-5275",
    "Open M-F 4:00-7:00 PM; call ahead: 720-735-5275",
    "$25",
  ],
  [
    "INTERVENTION",
    "12130 Pennsylvania St STE 102",
    "THORNTON",
    "303-450-6000",
    "M-F 7:30-4:45 CLOSED 11:45-12:30",
    "$22.5;",
  ],
] as const;

export const coTestingLocationListings: CoTestingLocationListing[] = [
  ...weldApprovedSiteRows.map(([providerName, addressLine1, city, postalCode, rawPhoneNumber]) =>
    createListing({
      documentId: "weld-county-approved-sites",
      providerName,
      addressLine1,
      city,
      postalCode,
      rawPhoneNumber,
    })
  ),
  ...interventionHoursRows.map((row) =>
    createListing({
      documentId: "intervention-drug-testing-hours",
      providerName: row.providerName,
      addressLine1: row.addressLine1,
      city: row.city,
      postalCode: row.postalCode,
      rawPhoneNumber: row.rawPhoneNumber,
      hours: row.hours,
      notes: row.notes,
    })
  ),
  ...jeffersonSiteRows.map(([providerName, addressLine1, city, rawPhoneNumber, hours, price]) =>
    createListing({
      documentId: "jefferson-county-ua-testing-sites",
      providerName,
      addressLine1,
      city,
      postalCode: "00000",
      rawPhoneNumber,
      hours,
      price,
    })
  ),
].map((listing) =>
  listing.documentId === "jefferson-county-ua-testing-sites"
    ? { ...listing, postalCode: listing.postalCode === "00000" ? "" : listing.postalCode }
    : listing
);

export function findCoTestingLocationListingsByDocumentId(
  documentId: string
): CoTestingLocationListing[] {
  return coTestingLocationListings.filter((listing) => listing.documentId === documentId);
}

export function findCoTestingLocationListingsByPhoneNumber(
  phoneNumber: string
): CoTestingLocationListing[] {
  return coTestingLocationListings.filter((listing) => listing.phoneNumber === phoneNumber);
}
